import { ReactElement } from 'react';
import { Modal } from 'antd';
import { FormattedMessage } from 'react-intl';

import { ReactComponent as CloseIcon } from 'app/assets/icons/close-black.svg';

import SingleTourChart from '../Charts/SingleTourChart/SingleTourChart';
import { getChartData } from '../getChartData';

import { Tour } from 'app/types';

import styles from './SingleTourModal.module.scss';

type ModalProps = {
  values: {
    isOpened: boolean;
    close: () => void;
  };
  data: Tour;
};

function SingleTourModal({ values, data }: ModalProps): ReactElement {
  if (!data?.name) return <div />;
  const [lastWeekData, lastMonthData, lastYearData] = getChartData([data]);
  if (lastYearData.length === 0) return <div />;
  const { isOpened, close } = values;
  const currentMonthViews = lastYearData[lastYearData.length - 1].Views;
  const previousMonthViews =
    lastYearData.length > 1 ? lastYearData[lastYearData.length - 2].Views : 0;
  let monthToMonthChange =
    previousMonthViews === 0 || currentMonthViews === 0
      ? 0
      : Math.round((currentMonthViews / previousMonthViews) * 100);
  if (monthToMonthChange < 100) {
    monthToMonthChange = 100 - monthToMonthChange;
  }

  return (
    <Modal
      width={1030}
      visible={isOpened}
      footer={null}
      centered={true}
      closeIcon={<CloseIcon />}
      wrapClassName={'insightsModal'}
      onCancel={close}
    >
      <div className={styles.wrapper}>
        <h1 className={styles.header}>{data.name}</h1>

        <SingleTourChart
          lastWeekData={lastWeekData}
          lastMonthData={lastMonthData}
          lastYearData={lastYearData}
        />

        <div className={styles.bottomBox}>
          <div>
            <p className={styles.title}>
              <FormattedMessage
                id="personalInsights.singleTourChart.currentMonth"
                defaultMessage="Current Month"
              />
            </p>
          </div>
          <div className={styles.countWrapper}>
            <p className={styles.bigText}>{currentMonthViews}</p>
            <p className={styles.text}>
              <FormattedMessage
                id="personalInsights.singleTourChart.views"
                defaultMessage="Views"
              />
            </p>
          </div>
          {monthToMonthChange !== 0 && (
            <div>
              {monthToMonthChange > 100 && (
                <p className={styles.text}>
                  <span className={styles.green}>+{monthToMonthChange}% </span>
                  <FormattedMessage
                    id="personalInsights.singleTourChart.currentMonth"
                    defaultMessage="more than at previous month"
                  />
                </p>
              )}
              {monthToMonthChange < 100 && (
                <p className={styles.text}>
                  <span className={styles.blue}>-{monthToMonthChange}% </span>
                  <FormattedMessage
                    id="personalInsights.singleTourChart.currentMonth"
                    defaultMessage="less than at previous month"
                  />
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default SingleTourModal;
